import { useNavigate } from 'react-router-dom';
import BusinessOutlined from '@mui/icons-material/BusinessOutlined';
import ErrorOutlineOutlined from '@mui/icons-material/ErrorOutlineOutlined';
import GroupAddOutlined from '@mui/icons-material/GroupAddOutlined';
import PeopleOutline from '@mui/icons-material/PeopleOutlined';
import { DirectoryPage } from '../components/DirectoryPage';
import { lastActivityColumn, statusColumn } from '../components/columns';
import { NameCell } from '../components/NameCell';
import { AddCustomerDialog } from '../features/customers/AddCustomerDialog';
import type { Customer } from '../features/customers/sample';
import { useCustomerStats } from '../features/customers/stats';
import { useCustomers } from '../features/customers/useCustomers';
import { pct } from '../lib/numbers';

const unique = (values: string[]) => [...new Set(values)].sort();
const share = (n: number, total: number) => `${n} (${pct(n, total)}%)`;

/** Every customer, with how their shipments are going. A click opens the customer. */
export function CustomersPage() {
  const navigate = useNavigate();
  const customers = useCustomers();
  const statsOf = useCustomerStats();
  const all = customers.items;
  return (
    <DirectoryPage<Customer>
      title="Customers"
      subtitle="Manage your customers, view their shipment history, and track opportunities."
      addLabel="Add Customer"
      noun="customers"
      items={all}
      tiles={{
        total: { title: 'Total Customers', note: 'All registered customers', icon: <PeopleOutline />, tone: 'info' },
        active: {
          title: 'Active Customers',
          note: 'With ongoing or recent shipments',
          icon: <BusinessOutlined />,
          tone: 'success',
        },
        fresh: {
          title: 'New Customers',
          note: 'Added in the last 30 days',
          icon: <GroupAddOutlined />,
          tone: 'warning',
        },
        attention: {
          title: 'Needs Attention',
          note: 'Outstanding quotations or follow-ups',
          icon: <ErrorOutlineOutlined />,
          tone: 'error',
        },
      }}
      needsAttention={(c) => statsOf(c).needsAttention}
      searchText={(c) =>
        [c.name, c.contact, c.email, c.industry, c.taxId, c.clearanceBroker, c.clearanceBrokerCustomsId].join(' ')
      }
      searchPlaceholder="Search customers by name, contact, email, industry, tax ID, clearance broker…"
      filters={[
        { label: 'Industry', options: unique(all.map((c) => c.industry)), matches: (c, v) => c.industry === v },
        { label: 'Country', options: unique(all.map((c) => c.country)), matches: (c, v) => c.country === v },
      ]}
      rowKey={(c) => c.id}
      onOpen={(c) => void navigate(`/customers/${c.id}`)}
      columns={[
        { header: 'Customer Name', primary: true, render: (c) => <NameCell name={c.name} /> },
        { header: 'Industry', render: (c) => c.industry },
        { header: 'Contact Person', render: (c) => c.contact },
        { header: 'Email', render: (c) => c.email },
        { header: 'Phone', nowrap: true, render: (c) => c.phone },
        { header: 'Active', nowrap: true, render: (c) => statsOf(c).active },
        { header: 'Total', nowrap: true, render: (c) => statsOf(c).total },
        { header: 'Arrived', nowrap: true, render: (c) => share(statsOf(c).arrived, statsOf(c).total) },
        { header: 'Lost', nowrap: true, render: (c) => share(statsOf(c).lost, statsOf(c).total) },
        lastActivityColumn((c) => statsOf(c).lastActivity),
        statusColumn(),
      ]}
      addDialog={(d) => <AddCustomerDialog {...d} onAdd={customers.add} />}
    />
  );
}
